import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import CallWidget from '../CallWidget';

/**
 * ToolsContent — the tools overlay body.
 *
 * Quick Create now has inline forms for ALL five entities that have a
 * "create new" flow on their respective pages: Candidate, Tag, Search,
 * Firm, Opportunity Role. Fields mirror the modal versions exactly, and
 * each submits to the same endpoint the page-level modal hits. On
 * success, we close the assistant and route to the created entity's
 * detail page (where one exists).
 *
 * Endpoints (verified against page modals):
 *   Candidate     → POST /api/candidates              (AddCandidateModal)
 *   Tag           → POST /api/internal/tags           (RecruiterTagsPage inline modal)
 *   Search        → POST /api/pipelines/create-search (SearchesPage inline modal)
 *   Firm          → POST /api/firms                   (FirmModal via firmController)
 *   Opportunity   → POST /api/opportunity-roles       (HandshakesPage inline modal)
 */

// ── Tools tile data ──────────────────────────────────────────────────────
const TOOLS = [
  { id: 'quickCreate', label: 'Quick Create', subtitle: 'Candidate, tag, search, firm, opportunity', kind: 'inline',
    icon: (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>) },
  { id: 'reminders', label: 'Reminders', subtitle: 'Follow-ups from candidate profiles', kind: 'inline',
    icon: (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>) },
  { id: 'activity', label: 'Activity Feed', subtitle: 'Recent changes + recently viewed', kind: 'inline',
    icon: (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>) },
  { id: 'communications', label: 'Call & SMS', subtitle: 'Outbound calls + one-off SMS', kind: 'inline',
    icon: (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>) },
];

// Order per user request: Candidate, Tag, Search, Firm, Opportunities
const CREATE_ENTITIES = [
  { id: 'candidate',   label: 'Candidate',   subtitle: 'Add a person to your archive' },
  { id: 'tag',         label: 'Tag',         subtitle: 'Create a new tag for candidates' },
  { id: 'search',      label: 'Search',      subtitle: 'Open a new executive search' },
  { id: 'firm',        label: 'Firm',        subtitle: 'Register a client or partner firm' },
  { id: 'opportunity', label: 'Opportunity', subtitle: 'Track a role in the market' },
];

// Tag colors — copied verbatim from RecruiterTagsPage.jsx (top of file)
const TAG_COLORS = [
  { name: 'blue',   dot: 'bg-blue-500'   },
  { name: 'green',  dot: 'bg-green-500'  },
  { name: 'purple', dot: 'bg-purple-500' },
  { name: 'yellow', dot: 'bg-yellow-500' },
  { name: 'pink',   dot: 'bg-pink-500'   },
  { name: 'indigo', dot: 'bg-indigo-500' },
  { name: 'orange', dot: 'bg-orange-500' },
  { name: 'teal',   dot: 'bg-teal-500'   },
  { name: 'red',    dot: 'bg-red-500'    },
  { name: 'gray',   dot: 'bg-gray-500'   },
];

// ── Shared UI ────────────────────────────────────────────────────────────
function CardButton({ icon, label, subtitle, onClick, soon = false }) {
  return (
    <button
      onClick={onClick}
      className={`p-3 border rounded-lg text-left transition-colors flex flex-col gap-2 ${
        soon ? 'border-gray-200 opacity-70 hover:bg-gray-50'
             : 'border-gray-200 hover:border-emerald-500 hover:bg-emerald-50'}`}
    >
      <div className="flex items-center justify-between">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          soon ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-700'}`}>{icon}</div>
        {soon && (
          <span className="text-[9px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded font-semibold uppercase tracking-wide">Soon</span>
        )}
      </div>
      <div>
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5 leading-snug">{subtitle}</div>
      </div>
    </button>
  );
}

const inputCls = "w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";
const labelCls = "block text-[11px] font-medium text-gray-600 mb-0.5";

// Reusable form action row (Cancel / Submit)
function FormActions({ onCancel, onSubmit, disabled, saving, submitLabel = 'Create' }) {
  return (
    <div className="flex items-center gap-2 justify-end pt-1">
      <button onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
      <button
        onClick={onSubmit}
        disabled={disabled || saving}
        className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        {saving ? 'Creating…' : submitLabel}
      </button>
    </div>
  );
}

// Reusable auth headers helper
const authJson = () => ({
  Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  'Content-Type': 'application/json',
});

// ── Candidate ────────────────────────────────────────────────────────────
function CandidateForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', linkedin: '',
    current_title: '', current_company: '', location: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/candidates`, {
        method: 'POST', headers: authJson(), body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create candidate');
      onSuccess?.(await res.json());
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="p-3 space-y-2.5">
      {error && <div className="px-2 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
      <div>
        <label className={labelCls}>Full Name <span className="text-red-500">*</span></label>
        <input value={form.name} onChange={upd('name')} placeholder="John Doe" className={inputCls} autoFocus />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className={labelCls}>Email</label><input type="email" value={form.email} onChange={upd('email')} placeholder="john@example.com" className={inputCls} /></div>
        <div><label className={labelCls}>Phone</label><input type="tel" value={form.phone} onChange={upd('phone')} placeholder="+1 (555) 123-4567" className={inputCls} /></div>
      </div>
      <div><label className={labelCls}>LinkedIn URL</label><input type="url" value={form.linkedin} onChange={upd('linkedin')} placeholder="https://linkedin.com/in/..." className={inputCls} /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className={labelCls}>Current Title</label><input value={form.current_title} onChange={upd('current_title')} placeholder="Senior Engineer" className={inputCls} /></div>
        <div><label className={labelCls}>Current Company</label><input value={form.current_company} onChange={upd('current_company')} placeholder="Tech Corp" className={inputCls} /></div>
      </div>
      <div><label className={labelCls}>Location</label><input value={form.location} onChange={upd('location')} placeholder="San Francisco, CA" className={inputCls} /></div>
      <FormActions onCancel={onCancel} onSubmit={submit} disabled={!form.name.trim()} saving={saving} />
    </div>
  );
}

// ── Tag ──────────────────────────────────────────────────────────────────
function TagForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState({ name: '', color: 'blue', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/internal/tags`, {
        method: 'POST', headers: authJson(), body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create tag');
      onSuccess?.(await res.json().catch(() => null));
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="p-3 space-y-2.5">
      {error && <div className="px-2 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
      <div>
        <label className={labelCls}>Tag Name <span className="text-red-500">*</span></label>
        <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g., Priority" className={inputCls} autoFocus />
      </div>
      <div>
        <label className={labelCls}>Color</label>
        <div className="flex flex-wrap gap-1.5">
          {TAG_COLORS.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => setForm((p) => ({ ...p, color: c.name }))}
              className={`w-7 h-7 rounded-full ${c.dot} transition-transform ${form.color === c.name ? 'ring-2 ring-offset-1 ring-gray-700 scale-110' : 'hover:scale-105'}`}
              title={c.name}
            />
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls}>Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          rows={2}
          placeholder="Optional description"
          className={inputCls + ' resize-none'}
        />
      </div>
      <FormActions onCancel={onCancel} onSubmit={submit} disabled={!form.name.trim()} saving={saving} />
    </div>
  );
}

// ── Search ───────────────────────────────────────────────────────────────
function SearchForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState({
    name: '', client_firm_id: '', description: '',
    requirements: '', location: '', compensation_range: '',
  });
  const [firms, setFirms] = useState([]);
  const [firmsLoading, setFirmsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load client firms for the required select
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/firms`, { headers: authJson() });
        if (res.ok) setFirms(await res.json());
      } catch (e) { /* silent */ } finally { setFirmsLoading(false); }
    })();
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.client_firm_id) return;
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/pipelines/create-search`, {
        method: 'POST', headers: authJson(), body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create search');
      onSuccess?.(await res.json());
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="p-3 space-y-2.5">
      {error && <div className="px-2 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
      <div>
        <label className={labelCls}>Search / Position Name <span className="text-red-500">*</span></label>
        <input value={form.name} onChange={upd('name')} placeholder="e.g., Chief Financial Officer" className={inputCls} autoFocus />
      </div>
      <div>
        <label className={labelCls}>Client Company <span className="text-red-500">*</span></label>
        <select value={form.client_firm_id} onChange={upd('client_firm_id')} className={inputCls} disabled={firmsLoading}>
          <option value="">{firmsLoading ? 'Loading firms…' : 'Select a client company…'}</option>
          {firms.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className={labelCls}>Location</label><input value={form.location} onChange={upd('location')} placeholder="San Francisco, CA" className={inputCls} /></div>
        <div><label className={labelCls}>Compensation</label><input value={form.compensation_range} onChange={upd('compensation_range')} placeholder="$200K-$300K" className={inputCls} /></div>
      </div>
      <div>
        <label className={labelCls}>Description</label>
        <textarea value={form.description} onChange={upd('description')} rows={2} placeholder="Overview of the role…" className={inputCls + ' resize-none'} />
      </div>
      <div>
        <label className={labelCls}>Requirements</label>
        <textarea value={form.requirements} onChange={upd('requirements')} rows={2} placeholder="Skills, experience, credentials…" className={inputCls + ' resize-none'} />
      </div>
      <FormActions onCancel={onCancel} onSubmit={submit} disabled={!form.name.trim() || !form.client_firm_id} saving={saving} />
    </div>
  );
}

// ── Firm ─────────────────────────────────────────────────────────────────
function FirmForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState({
    name: '', founded: new Date().getFullYear(), linkedinUrl: '', website: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/firms`, {
        method: 'POST', headers: authJson(),
        body: JSON.stringify({
          name: form.name,
          founded: form.founded ? parseInt(form.founded, 10) : null,
          linkedinUrl: form.linkedinUrl || null,
          website: form.website || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create firm');
      onSuccess?.(await res.json());
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="p-3 space-y-2.5">
      {error && <div className="px-2 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
      <div>
        <label className={labelCls}>Firm Name <span className="text-red-500">*</span></label>
        <input value={form.name} onChange={upd('name')} placeholder="Acme Capital" className={inputCls} autoFocus />
      </div>
      <div><label className={labelCls}>Founded</label><input type="number" value={form.founded} onChange={upd('founded')} placeholder="2015" className={inputCls} /></div>
      <div><label className={labelCls}>Website</label><input type="url" value={form.website} onChange={upd('website')} placeholder="https://example.com" className={inputCls} /></div>
      <div><label className={labelCls}>LinkedIn URL</label><input type="url" value={form.linkedinUrl} onChange={upd('linkedinUrl')} placeholder="https://linkedin.com/company/..." className={inputCls} /></div>
      <FormActions onCancel={onCancel} onSubmit={submit} disabled={!form.name.trim()} saving={saving} />
    </div>
  );
}

// ── Opportunity Role ─────────────────────────────────────────────────────
function OpportunityForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState({
    title: '', company: '', location: '', description: '',
    source: '', contact_name: '', contact_email: '', url: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/opportunity-roles`, {
        method: 'POST', headers: authJson(), body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create opportunity');
      onSuccess?.(await res.json().catch(() => null));
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };
  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="p-3 space-y-2.5">
      {error && <div className="px-2 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
      <div>
        <label className={labelCls}>Role Title <span className="text-red-500">*</span></label>
        <input value={form.title} onChange={upd('title')} placeholder="Head of Product" className={inputCls} autoFocus />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className={labelCls}>Company</label><input value={form.company} onChange={upd('company')} placeholder="Company name" className={inputCls} /></div>
        <div><label className={labelCls}>Location</label><input value={form.location} onChange={upd('location')} placeholder="San Francisco, CA" className={inputCls} /></div>
      </div>
      <div><label className={labelCls}>Source</label><input value={form.source} onChange={upd('source')} placeholder="How you heard about it" className={inputCls} /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className={labelCls}>Contact Name</label><input value={form.contact_name} onChange={upd('contact_name')} placeholder="Jane Smith" className={inputCls} /></div>
        <div><label className={labelCls}>Contact Email</label><input type="email" value={form.contact_email} onChange={upd('contact_email')} placeholder="jane@company.com" className={inputCls} /></div>
      </div>
      <div><label className={labelCls}>URL / Job Posting</label><input type="url" value={form.url} onChange={upd('url')} placeholder="https://..." className={inputCls} /></div>
      <div>
        <label className={labelCls}>Notes</label>
        <textarea value={form.description} onChange={upd('description')} rows={2} placeholder="Notes / description" className={inputCls + ' resize-none'} />
      </div>
      <FormActions onCancel={onCancel} onSubmit={submit} disabled={!form.title.trim()} saving={saving} />
    </div>
  );
}

// ── Reminders tool ──────────────────────────────────────────────────────
// Standalone entry point for setting a follow-up reminder on any
// candidate. Unlike CandidatePreviewPanel's reminder button (which is
// scoped to the currently-open candidate), this tool starts with a
// candidate search. Once a candidate is selected, the duration picker
// is identical to the panel's dropdown — 5 predefined durations plus a
// custom-days input, POSTing to /api/reminders.
function RemindersTool({ onDone }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [customDays, setCustomDays] = useState('');
  // Optional free-text note. Same purpose as the header dropdown's
  // reminderNote — gives the recruiter context when the reminder fires.
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Debounced candidate search — 250ms after the user stops typing.
  useEffect(() => {
    if (selectedCandidate) return; // pause search once a candidate is picked
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`/api/candidate-archive/internal-archive?search=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        // Archive endpoint returns { candidates: [...] } or an array —
        // handle both defensively.
        const list = Array.isArray(data) ? data : (data.candidates || []);
        if (!cancelled) setResults(list.slice(0, 8));
      } catch (e) {
        console.error('[banyak-reminder-tool] search failed', e);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, selectedCandidate]);

  const createReminder = async (days) => {
    if (!selectedCandidate || saving) return;
    // Archive endpoint returns candidate_id (not id) — match the fallback
    // pattern used by CandidateArchive throughout the app.
    const cid = selectedCandidate.candidate_id || selectedCandidate.id;
    if (!cid) {
      setMessage('Could not identify candidate — pick another one');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidate_id: cid, days_from_now: days, note: note.trim() || undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not set reminder');
      }
      const created = await res.json();
      const when = new Date(created.remind_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      setMessage(`✓ Reminder set for ${when}`);
      // Close the tool after briefly showing success — matches the
      // CandidatePreviewPanel dropdown's auto-close pattern.
      setTimeout(() => { onDone?.(); }, 1500);
    } catch (e) {
      console.error('[banyak-reminder-tool] create failed', e);
      setMessage(e.message || 'Failed to set reminder');
    } finally {
      setSaving(false);
    }
  };

  const clearSelection = () => {
    setSelectedCandidate(null);
    setResults([]);
    setQuery('');
    setMessage('');
    setNote('');
    setCustomDays('');
  };

  return (
    <div className="p-4 space-y-3">
      {/* Step 1 — candidate search (hidden once a candidate is selected) */}
      {!selectedCandidate && (
        <>
          <label className={labelCls}>Find a candidate</label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name…"
            className={inputCls}
            autoFocus
          />
          {searching && query.trim().length >= 2 && (
            <div className="text-xs text-gray-400">Searching…</div>
          )}
          {results.length > 0 && (
            <div className="border rounded-lg divide-y bg-white overflow-hidden">
              {results.map(c => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedCandidate(c); setResults([]); }}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-orange-50"
                >
                  {c.profile_picture_url || c.avatar_url ? (
                    <img src={c.profile_picture_url || c.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-500 font-medium flex-shrink-0">
                      {c.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-gray-900 truncate">{c.name}</div>
                    {(c.current_company || c.current_title) && (
                      <div className="text-[10px] text-gray-400 truncate">
                        {[c.current_title, c.current_company].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {query.trim().length >= 2 && !searching && results.length === 0 && (
            <div className="text-xs text-gray-400 italic">No candidates match "{query}"</div>
          )}
        </>
      )}

      {/* Step 2 — duration picker (shown once a candidate is selected) */}
      {selectedCandidate && (
        <>
          <div className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded">
            {selectedCandidate.profile_picture_url || selectedCandidate.avatar_url ? (
              <img src={selectedCandidate.profile_picture_url || selectedCandidate.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-orange-200 flex items-center justify-center text-[11px] text-orange-700 font-semibold flex-shrink-0">
                {selectedCandidate.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-gray-900 truncate">{selectedCandidate.name}</div>
              {(selectedCandidate.current_company || selectedCandidate.current_title) && (
                <div className="text-[10px] text-gray-500 truncate">
                  {[selectedCandidate.current_title, selectedCandidate.current_company].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            <button onClick={clearSelection} title="Change candidate"
              className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-white flex-shrink-0">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Remind me in</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { label: '2 weeks',   days: 14 },
                { label: '1 month',   days: 30 },
                { label: '3 months',  days: 90 },
                { label: '6 months',  days: 180 },
                { label: '1 year',    days: 365 },
              ].map(opt => (
                <button key={opt.days}
                  onClick={() => createReminder(opt.days)}
                  disabled={saving}
                  className="px-2 py-1.5 text-xs text-gray-700 border border-gray-200 rounded hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 disabled:opacity-50"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Custom (days)</div>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="1"
                placeholder="45"
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { const d = parseInt(customDays); if (d > 0) createReminder(d); } }}
                className={inputCls}
              />
              <button
                onClick={() => { const d = parseInt(customDays); if (d > 0) createReminder(d); }}
                disabled={saving || !parseInt(customDays)}
                className="px-3 py-1.5 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex-shrink-0"
              >
                Set
              </button>
            </div>
          </div>

          {/* Optional reason — same purpose as the header dropdown's
              reason field. Sent as `note` in the POST body when the
              user picks a duration. */}
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Reason (optional)</div>
            <textarea
              rows={2}
              placeholder="e.g. check hiring signal"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={`${inputCls} resize-none`}
            />
          </div>

          {message && (
            <div className={`text-xs px-2 py-1.5 rounded ${message.startsWith('✓') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {message}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────
export default function ToolsContent({ onClose }) {
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState(null);
  // activeCreate: null | 'candidate' | 'tag' | 'search' | 'firm' | 'opportunity'
  const [activeCreate, setActiveCreate] = useState(null);
  // Success banner shown on the Quick Create picker after creating
  // something. Auto-dismisses after 4s; also clears when user leaves
  // the picker or creates something else.
  const [successMsg, setSuccessMsg] = useState(null);

  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  const handleBack = () => {
    if (activeCreate) { setActiveCreate(null); return; }
    setActiveTool(null);
    setSuccessMsg(null);
  };

  // On successful create: DON'T redirect. Show an inline success
  // banner and return the user to the Quick Create picker so they can
  // keep creating things. If they want to see the new entity, they can
  // navigate there themselves via the top-bar nav or search.
  const handleCreateSuccess = (created) => {
    const label = {
      candidate:   'Candidate',
      tag:         'Tag',
      search:      'Search',
      firm:        'Firm',
      opportunity: 'Opportunity',
    }[activeCreate] || 'Item';
    setSuccessMsg(`${label} created successfully.`);
    setActiveCreate(null); // back to picker
  };

  const headerTitle = activeCreate
    ? `New ${activeCreate.charAt(0).toUpperCase() + activeCreate.slice(1)}`
    : activeTool ? activeTool.label : 'Tools';

  return (
    <div className="absolute inset-0 z-20 bg-white flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 flex items-center gap-2">
        {(activeTool || activeCreate) && (
          <button onClick={handleBack} className="p-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700" title="Back">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">{headerTitle}</span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {/* Inline create forms */}
        {activeCreate === 'candidate'   && <CandidateForm   onSuccess={handleCreateSuccess} onCancel={() => setActiveCreate(null)} />}
        {activeCreate === 'tag'         && <TagForm         onSuccess={handleCreateSuccess} onCancel={() => setActiveCreate(null)} />}
        {activeCreate === 'search'      && <SearchForm      onSuccess={handleCreateSuccess} onCancel={() => setActiveCreate(null)} />}
        {activeCreate === 'firm'        && <FirmForm        onSuccess={handleCreateSuccess} onCancel={() => setActiveCreate(null)} />}
        {activeCreate === 'opportunity' && <OpportunityForm onSuccess={handleCreateSuccess} onCancel={() => setActiveCreate(null)} />}

        {/* Default: tool cards */}
        {!activeTool && !activeCreate && (
          <div className="p-3">
            <div className="grid grid-cols-2 gap-3">
              {TOOLS.map((tool) => (
                <CardButton
                  key={tool.id}
                  icon={tool.icon}
                  label={tool.label}
                  subtitle={tool.subtitle}
                  soon={tool.kind === 'coming-soon'}
                  onClick={() => setActiveTool(tool)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Quick Create entity picker — shows success banner above the
            grid when a create has just landed. */}
        {activeTool && activeTool.id === 'quickCreate' && !activeCreate && (
          <div className="p-3">
            {successMsg && (
              <div className="mb-3 flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{successMsg}</span>
                </div>
                <button onClick={() => setSuccessMsg(null)} className="text-emerald-700 hover:text-emerald-900" title="Dismiss">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 mb-3">Pick what to create.</p>
            <div className="grid grid-cols-2 gap-3">
              {CREATE_ENTITIES.map((entity) => (
                <button
                  key={entity.id}
                  onClick={() => { setActiveCreate(entity.id); setSuccessMsg(null); }}
                  className="p-3 border border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-left"
                >
                  <div className="text-sm font-semibold text-gray-900">{entity.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-snug">{entity.subtitle}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTool && activeTool.id === 'reminders' && (
          <RemindersTool onDone={() => setActiveTool(null)} />
        )}

        {activeTool && activeTool.id === 'activity' && (
          <ActivityTool />
        )}
        {activeTool && activeTool.id === 'communications' && (
          <CommunicationsTool />
        )}
      </div>
    </div>
  );
}

// ── Communications tool (Call & SMS) ────────────────────────────────
// Candidate search → then two panels: Call (phones with call buttons,
// opens the existing CallWidget) and SMS (phone picker + textarea +
// send). Mirrors the CandidatePreviewPanel behavior but in a modeless
// tool so recruiters don't have to open a full candidate view to make
// a quick call or send a one-off SMS.
function CommunicationsTool() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [phones, setPhones] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);

  // Call widget state — mirrors CPP
  const [showCallWidget, setShowCallWidget] = useState(false);
  const [callPhone, setCallPhone] = useState('');

  // SMS state
  const [smsPhone, setSmsPhone] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');

  // Debounced candidate search (250ms after last keystroke)
  useEffect(() => {
    if (selectedCandidate) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`/api/candidate-archive/internal-archive?search=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.candidates || []);
        if (!cancelled) setResults(list.slice(0, 8));
      } catch (e) {
        console.error('[banyak-comms-tool] search failed', e);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, selectedCandidate]);

  // Load full profile → get phone numbers when a candidate is picked
  useEffect(() => {
    if (!selectedCandidate) return;
    const cid = selectedCandidate.candidate_id || selectedCandidate.id;
    if (!cid) return;
    setProfileLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`/api/client/candidates/${cid}/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const arr = data?.client_data?.phones || [];
          const cleaned = (Array.isArray(arr) ? arr : []).filter(p => p.value);
          setPhones(cleaned);
          // Default SMS to primary phone if available
          const primary = cleaned.find(p => p.is_primary) || cleaned[0];
          if (primary) setSmsPhone(primary.value);
        } else {
          setPhones([]);
        }
      } catch (e) {
        console.error('[banyak-comms-tool] profile load failed', e);
        setPhones([]);
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [selectedCandidate]);

  const clearSelection = () => {
    setSelectedCandidate(null);
    setPhones([]);
    setResults([]);
    setQuery('');
    setSmsPhone('');
    setSmsBody('');
    setSmsMessage('');
  };

  const startCall = (phone) => {
    setCallPhone(phone);
    setShowCallWidget(true);
  };

  const sendSms = async () => {
    if (!smsPhone || !smsBody.trim() || smsSending) return;
    const cid = selectedCandidate?.candidate_id || selectedCandidate?.id;
    setSmsSending(true);
    setSmsMessage('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/outreach/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidate_id: cid, to: smsPhone, body: smsBody.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send SMS');
      setSmsMessage(`✓ SMS sent (${data.status || 'queued'})`);
      setSmsBody('');
    } catch (e) {
      console.error('[banyak-comms-tool] SMS send failed', e);
      setSmsMessage(e.message || 'Failed to send SMS');
    } finally {
      setSmsSending(false);
    }
  };

  return (
    <div className="p-4 space-y-3">
      {!selectedCandidate && (
        <>
          <label className={labelCls}>Find a candidate</label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name…"
            className={inputCls}
            autoFocus
          />
          {searching && query.trim().length >= 2 && (
            <div className="text-xs text-gray-400">Searching…</div>
          )}
          {results.length > 0 && (
            <div className="border rounded-lg divide-y bg-white overflow-hidden">
              {results.map(c => (
                <button
                  key={c.candidate_id || c.id}
                  onClick={() => setSelectedCandidate(c)}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-teal-50"
                >
                  {c.profile_picture_url || c.avatar_url ? (
                    <img src={c.profile_picture_url || c.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-500 font-medium flex-shrink-0">
                      {c.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-gray-900 truncate">{c.name}</div>
                    {(c.current_company || c.current_title) && (
                      <div className="text-[10px] text-gray-400 truncate">
                        {[c.current_title, c.current_company].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {query.trim().length >= 2 && !searching && results.length === 0 && (
            <div className="text-xs text-gray-400 italic">No candidates match "{query}"</div>
          )}
        </>
      )}

      {selectedCandidate && (
        <>
          {/* Candidate card */}
          <div className="flex items-center gap-2 p-2 bg-teal-50 border border-teal-200 rounded">
            {selectedCandidate.profile_picture_url || selectedCandidate.avatar_url ? (
              <img src={selectedCandidate.profile_picture_url || selectedCandidate.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-teal-200 flex items-center justify-center text-[11px] text-teal-700 font-semibold flex-shrink-0">
                {selectedCandidate.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-gray-900 truncate">{selectedCandidate.name}</div>
              {(selectedCandidate.current_company || selectedCandidate.current_title) && (
                <div className="text-[10px] text-gray-500 truncate">
                  {[selectedCandidate.current_title, selectedCandidate.current_company].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
            <button onClick={clearSelection} title="Change candidate"
              className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-white flex-shrink-0">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Call section */}
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Call</div>
            {profileLoading ? (
              <div className="text-xs text-gray-400 py-2">Loading phone numbers…</div>
            ) : phones.length === 0 ? (
              <div className="text-xs text-gray-400 py-2 italic">No phone numbers on file for this candidate.</div>
            ) : (
              <div className="space-y-1.5">
                {phones.map((p, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border border-gray-200 rounded">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900 font-mono">{p.value}</div>
                      {p.type && <div className="text-[10px] text-gray-500">{p.type}{p.is_primary && ' · primary'}</div>}
                    </div>
                    <button
                      onClick={() => startCall(p.value)}
                      className="px-3 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 flex-shrink-0"
                    >
                      Call
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SMS composer — always available; user can type any number
              if phones list is empty */}
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Send SMS</div>
            {phones.length > 0 ? (
              <select
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
                className={`${inputCls} mb-1.5`}
              >
                {phones.map((p, idx) => (
                  <option key={idx} value={p.value}>
                    {p.value}{p.type ? ` (${p.type})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
                className={`${inputCls} mb-1.5`}
              />
            )}
            <textarea
              rows={3}
              placeholder="Type your message…"
              value={smsBody}
              onChange={(e) => setSmsBody(e.target.value)}
              className={`${inputCls} resize-none`}
              maxLength={1600}
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] text-gray-400">{smsBody.length} / 1600</span>
              <button
                onClick={sendSms}
                disabled={smsSending || !smsPhone.trim() || !smsBody.trim()}
                className="px-3 py-1.5 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
              >
                {smsSending ? 'Sending…' : 'Send SMS'}
              </button>
            </div>
            {smsMessage && (
              <div className={`text-xs px-2 py-1.5 rounded mt-2 ${smsMessage.startsWith('✓') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {smsMessage}
              </div>
            )}
          </div>

          {/* CallWidget renders in a fixed bottom-right position when
              active — same pattern the CandidatePreviewPanel uses so the
              call UI behaves identically no matter where it was invoked. */}
          {showCallWidget && (
            <div className="fixed bottom-4 right-4 z-50">
              <CallWidget
                candidateId={selectedCandidate.candidate_id || selectedCandidate.id}
                candidateName={selectedCandidate.name || ''}
                phoneNumber={callPhone}
                onClose={() => setShowCallWidget(false)}
                onCallComplete={() => setShowCallWidget(false)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Activity tool ───────────────────────────────────────────────────
// Firm-wide activity feed with per-user + per-type filters. Fetches
// /api/activity/firm (unions notes, tasks, outreach events, SMS, calls)
// and /api/activity/users for the filter dropdown. Items show who did
// what and when, with a candidate link when applicable.
function ActivityTool() {
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterUser, setFilterUser] = useState(''); // '' = all users
  const [filterType, setFilterType] = useState(''); // '' = all types

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (filterUser) params.set('user_id', filterUser);
      if (filterType) params.set('type', filterType);
      params.set('limit', '100');
      const res = await fetch(`/api/activity/firm?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { setError('Failed to load activity feed'); return; }
      const data = await res.json();
      setActivities(data.activities || []);
    } catch (e) {
      console.error('[banyak-activity-tool] load failed', e);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  // Load users once
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`/api/activity/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUsers(data.users || []);
        }
      } catch (e) { console.error('[banyak-activity-tool] users load failed', e); }
    })();
  }, []);

  // Load activity feed whenever filters change
  useEffect(() => { load(); }, [filterUser, filterType]);

  // Icon + color for each activity type
  const typeVisual = (type) => {
    switch (type) {
      case 'note':  return { color: 'text-purple-600 bg-purple-50', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' };
      case 'task':  return { color: 'text-emerald-600 bg-emerald-50', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' };
      case 'email': return { color: 'text-amber-600 bg-amber-50', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' };
      case 'sms':   return { color: 'text-teal-600 bg-teal-50', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' };
      case 'call':  return { color: 'text-blue-600 bg-blue-50', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' };
      default:      return { color: 'text-gray-500 bg-gray-100', icon: 'M13 10V3L4 14h7v7l9-11h-7z' };
    }
  };

  // Relative time formatter — matches TasksContent
  const formatRelative = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    const s = Math.floor((Date.now() - then) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    const d = Math.floor(s / 86400);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d > 365 ? 'numeric' : undefined });
  };

  return (
    <div className="p-3 space-y-3">
      {/* Filters */}
      <div className="flex items-center gap-2">
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="flex-1 text-xs border rounded px-2 py-1"
        >
          <option value="">All users</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="flex-1 text-xs border rounded px-2 py-1"
        >
          <option value="">All activity</option>
          <option value="note">Notes</option>
          <option value="task">Tasks</option>
          <option value="email">Emails</option>
          <option value="sms">SMS</option>
          <option value="call">Calls</option>
        </select>
      </div>

      {loading ? (
        <div className="py-8 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
        </div>
      ) : error ? (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>
      ) : activities.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-sm text-gray-500 font-medium">No activity yet</p>
          <p className="text-xs text-gray-400 mt-1">
            {filterUser || filterType ? 'Try clearing the filters.' : 'Activity appears here as your team works.'}
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {activities.map(a => {
            const v = typeVisual(a.type);
            return (
              <div key={`${a.type}-${a.id}`} className="flex items-start gap-2 p-2 rounded hover:bg-gray-50">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${v.color}`}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={v.icon} />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    {a.user_avatar ? (
                      <img src={a.user_avatar} alt="" className="w-4 h-4 rounded-full object-cover inline-block flex-shrink-0" />
                    ) : null}
                    <span className="text-xs font-medium text-gray-900">{a.user_name || 'Someone'}</span>
                    {a.candidate_name && (
                      <span className="text-[11px] text-gray-500">→ {a.candidate_name}</span>
                    )}
                  </div>
                  {a.description && (
                    <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">{a.description}</div>
                  )}
                  <div className="text-[10px] text-gray-400 mt-0.5">{formatRelative(a.timestamp)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
